import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_COLORS = {
  // Canonical lifecycle. "Pending Payment" was retired in commit ab7dbc1
  // (no row is ever inserted in that state anymore — the request flow now
  // only persists a row after Showami succeeds). Kept the lowercase
  // legacy key purely so any historical straggler row in the DB still
  // renders rather than flashing untyped, but we no longer expose it as
  // a selectable status anywhere in the UI.
  'Pending':            { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  'Accepted':           { bg: '#3a6ad9', color: '#ffffff', label: 'Accepted'  },
  // Confirmed uses the same Doorly blue as Accepted but with a darker outline
  // shade so badges read as a tighter "in progress" pair while staying distinct.
  'Confirmed':          { bg: '#1e40af', color: '#ffffff', label: 'Confirmed' },
  'Completed':          { bg: '#acf44e', color: '#1a1a1a', label: 'Completed' },
  'Canceled':           { bg: '#ff082f', color: '#ffffff', label: 'Canceled'  },
  pending_confirmation: { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  pending:              { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  accepted:             { bg: '#3a6ad9', color: '#ffffff', label: 'Accepted'  },
  confirmed:            { bg: '#1e40af', color: '#ffffff', label: 'Confirmed' },
  completed:            { bg: '#acf44e', color: '#1a1a1a', label: 'Completed' },
  canceled:             { bg: '#ff082f', color: '#ffffff', label: 'Canceled'  },
  cancelled:            { bg: '#ff082f', color: '#ffffff', label: 'Canceled'  },
  // Legacy fallback only — pending_payment was retired and rows in this
  // state are auto-migrated to Pending on next server boot. Retained so
  // any in-flight row from before the deploy still renders gracefully.
  pending_payment:      { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
};

export default function AdminShowingRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Per-showing message threads. Lazy-loaded the first time a row is
  // expanded; cached after that so toggling closed/open doesn't re-fetch.
  const [messagesById, setMessagesById] = useState({});
  // Reply-box state for the messenger UI. Only one row is expanded at
  // a time (expandedId is a scalar) so a single set of state for the
  // active reply is sufficient.
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const messagesBottomRef = useRef(null);

  // Multi-select status pills. Default hides Canceled + Completed —
  // RE desk almost never needs those on first load. Empty set means
  // "no status constraint" (show every row) so deselecting all pills
  // is a quick way to see Canceled/Completed without clicking each.
  const DEFAULT_STATUSES = ['Pending', 'Accepted', 'Confirmed'];
  const [filterStatuses, setFilterStatuses] = useState(() => new Set(DEFAULT_STATUSES));
  const toggleStatus = (s) => {
    setFilterStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/showing-requests`, { headers });
      setRequests(res.data.requests || []);
    } catch {}
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Page-level mark-read on mount so the AdminSidebar showingRequests
  // badge clears the moment the admin lands on this view. The same
  // PATCH also flips is_read_admin=TRUE on every unread agent message,
  // which keeps the badge math consistent (showing_breakdown is the
  // sum of pending_review + status_transitions + unread_messages, all
  // of which collapse to zero after this call).
  useEffect(() => {
    axios.patch(`${API}/api/admin/showings/mark-read`, {}, { headers }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    // Reset reply-box state on row switch so a draft typed in one row
    // doesn't carry over to another (and any prior send error clears).
    setReplyText('');
    setReplyError(null);
    setEditMap((prev) => {
      if (prev[id]) return prev;
      const req = requests.find((r) => r.id === id);
      if (!req) return prev;
      return { ...prev, [id]: { admin_notes: req.admin_notes || '' } };
    });
    // Lazy-load the message thread for this showing the first time it's
    // expanded. Reconcile-on-open: GET /sync-messages first so any
    // backlog the webhook missed lands in showing_messages, then GET
    // /messages to render the now-current thread. This makes the
    // thread self-healing — even if Showami's webhook URL is stale or
    // a delivery was dropped, opening the row pulls the truth from
    // their API. Per-row mark-read on the same expand so the unread
    // dot clears immediately.
    if (messagesById[id] === undefined) {
      axios.get(`${API}/api/admin/showings/${id}/sync-messages`, { headers })
        .catch(() => {}) // sync failure shouldn't block render — the local thread renders from whatever's already in showing_messages
        .then(() => axios.get(`${API}/api/admin/showings/${id}/messages`, { headers }))
        .then((r) => setMessagesById((prev) => ({ ...prev, [id]: r.data.messages || [] })))
        .catch(() => setMessagesById((prev) => ({ ...prev, [id]: [] })));
    }
    axios.patch(`${API}/api/admin/showings/mark-read`, { id }, { headers }).catch(() => {});
  };

  const handleSave = async (id) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/api/admin/borrowers/showing-requests/${id}`, editMap[id], { headers });
      await fetchRequests();
    } catch {}
    setSavingId(null);
  };

  // iMessage-style timestamp: "10:34 AM" if today, otherwise "May 9 10:34 AM".
  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const isToday = d.toDateString() === new Date().toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Refetch the thread for one showing — used after a send so the
  // newly-inserted row appears immediately.
  const fetchShowingMessages = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/api/admin/showings/${id}/messages`, { headers });
      setMessagesById((prev) => ({ ...prev, [id]: r.data.messages || [] }));
    } catch (_) { /* non-fatal — leave existing cache as-is */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendShowingMessage = async () => {
    if (!replyText.trim() || sendingReply || !expandedId) return;
    setSendingReply(true);
    setReplyError(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/showings/${expandedId}/send-message`,
        { message: replyText.trim() },
        { headers }
      );
      // Optimistic append using the row the server just inserted, so
      // the bubble shows in the thread without waiting for the refetch.
      const inserted = r.data?.message;
      if (inserted) {
        setMessagesById((prev) => {
          const cur = prev[expandedId] || [];
          return { ...prev, [expandedId]: [...cur, {
            id: inserted.id,
            sender_type: inserted.sender_type || 'doorly',
            sender_name: inserted.sender_name || 'Doorly Team',
            message_text: inserted.message_text,
            received_at: inserted.received_at || new Date().toISOString(),
          }] };
        });
      }
      setReplyText('');
      // Refetch to pick up canonical timestamps + any messages that
      // landed concurrently via webhook between the optimistic append
      // and now.
      await fetchShowingMessages(expandedId);
    } catch (err) {
      setReplyError(err.response?.data?.error || err.message || 'Failed to send.');
    } finally {
      setSendingReply(false);
    }
  };

  // Auto-scroll the thread to the latest message whenever the list
  // for the active row changes (initial load, send, or webhook sync).
  useEffect(() => {
    if (!expandedId) return;
    if (messagesBottomRef.current) {
      messagesBottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [expandedId, messagesById]);

  const pendingCount = requests.filter((r) => r.status === 'Pending').length;

  const filtered = requests.filter((r) => {
    // Empty filterStatuses Set = no status constraint (show all rows).
    // Non-empty = row must have one of the selected statuses.
    if (filterStatuses.size > 0 && !filterStatuses.has(r.status)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q);
      const matchLoan = (r.loan_number || '').toLowerCase().includes(q);
      const matchAddress = (r.address || '').toLowerCase().includes(q);
      const matchMLS = (r.mls_number || '').toLowerCase().includes(q);
      if (!matchName && !matchLoan && !matchAddress && !matchMLS) return false;
    }
    if (filterStartDate && new Date(r.created_at) < new Date(filterStartDate + 'T00:00:00')) return false;
    if (filterEndDate && new Date(r.created_at) > new Date(filterEndDate + 'T23:59:59')) return false;
    return true;
  });

  // "Default" status filter = the 3-element pre-filtered set. Anything
  // different (including an empty set or extra statuses selected) counts
  // as a filter the user explicitly applied.
  const isDefaultStatusSet = filterStatuses.size === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((s) => filterStatuses.has(s));
  const hasFilters = !isDefaultStatusSet || searchQuery || filterStartDate || filterEndDate;
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStatuses(new Set(DEFAULT_STATUSES));
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 24px 40px', maxWidth: '1100px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>Showing Requests</h1>
          {pendingCount > 0 && (
            <span style={{ backgroundColor: '#ff082f', color: '#fff', borderRadius: '9999px', fontSize: '11px', fontWeight: '700', padding: '2px 8px' }}>
              {pendingCount} pending
            </span>
          )}
        </div>

        {/* Filter Bar */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Search</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#9ca3af' }}>🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Borrower name, loan #, address, MLS#..."
                style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          {/* Filter pills — match AdminOfferRequests style: solid saturated
              backgrounds with white/black text when active, 11px / 3px·10px
              padding, inline "Status:" label, no checkmark. Colors mirror the
              per-row STATUS_COLORS badges so the active filter reads as the
              same color as the rows it surfaces. */}
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: 6, flexBasis: '100%',
          }}>
            <span style={{
              fontSize: 11, color: '#9ca3af',
              fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.05em',
              alignSelf: 'center', marginRight: 4,
            }}>
              Status:
            </span>

            {filterStatuses.size === 0 && (
              <span style={{
                fontSize: 11, color: '#9ca3af',
                fontStyle: 'italic', alignSelf: 'center',
              }}>
                Showing all statuses
              </span>
            )}

            {[
              { value: 'Pending',   label: 'Pending',
                bg: '#9ca3af', text: '#ffffff', border: '#9ca3af' },
              { value: 'Accepted',  label: 'Accepted',
                bg: '#3a6ad9', text: '#ffffff', border: '#3a6ad9' },
              { value: 'Confirmed', label: 'Confirmed',
                bg: '#1e40af', text: '#ffffff', border: '#1e40af' },
              { value: 'Completed', label: 'Completed',
                bg: '#acf44e', text: '#1a1a1a', border: '#acf44e' },
              { value: 'Canceled',  label: 'Canceled',
                bg: '#ff082f', text: '#ffffff', border: '#ff082f' },
            ].map(s => {
              const active = filterStatuses.has(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleStatus(s.value)}
                  style={{
                    fontSize: 11, fontWeight: 600,
                    padding: '3px 10px',
                    borderRadius: 20,
                    border: `1px solid ${active ? s.border : '#e5e7eb'}`,
                    background: active ? s.bg : '#ffffff',
                    color: active ? s.text : '#9ca3af',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>From</label>
            <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>To</label>
            <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          {hasFilters && (
            <button onClick={clearFilters}
              style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-end' }}>
              ✕ Clear
            </button>
          )}
          <div style={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Showing <strong style={{ color: '#111827' }}>{filtered.length}</strong> of {requests.length}
            </span>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
            {requests.length === 0 ? 'No showing requests yet.' : 'No requests match your filters.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Column header row — mirrors the collapsed-card grid
                track-for-track so labels stay aligned with the row
                values below. Uppercase / muted styling keeps it from
                competing visually with the card content. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 160px 60px 120px 110px',
              gap: '12px',
              alignItems: 'center',
              padding: '0 20px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              <span>Borrower</span>
              <span>Address</span>
              <span>Preferred</span>
              <span style={{ textAlign: 'center' }}>Credits</span>
              <span style={{ textAlign: 'center' }}>Status</span>
              <span style={{ textAlign: 'right' }}>Submitted</span>
            </div>
            {filtered.map((req) => {
              const s = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
              const isOpen = expandedId === req.id;
              const edit = editMap[req.id] || {};

              // Compose the full address for display. Showami-sourced
              // rows store the street in `address` and city/state/zip in
              // separate property_* columns (see backend/routes/showami.js
              // INSERT) — borrower-submitted rows already pack the full
              // string into `address` and leave property_* NULL. The
              // presence of property_city is the cleanest discriminator:
              // when set, append the locality parts so the row matches
              // the offer-requests "123 Main St, Austin, TX 78701" shape;
              // otherwise render `address` as-is to avoid duplication.
              const fullAddress = (() => {
                if (!req.address) return '—';
                if (!req.property_city) return req.address;
                const stateZip = `${req.property_state || ''} ${req.property_zip || ''}`.trim();
                return [req.address, req.property_city, stateZip].filter(Boolean).join(', ');
              })();
              return (
                <div key={req.id} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <button
                    onClick={() => toggleExpand(req.id)}
                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 160px 60px 120px 110px', gap: '12px', alignItems: 'center', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                      {[req.first_name, req.last_name].filter(Boolean).join(' ') || 'Unknown Borrower'}
                    </span>
                    <span style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fullAddress}>
                      {fullAddress}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {/* ⚠️ "—" READ AS "something is missing". A borrower who
                          said "not sure yet" gave an answer, and it is the one
                          that needs a call rather than a confirmation. */}
                      {req.preferred_date
                        ? new Date(req.preferred_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'Time to be arranged'}
                      {req.preferred_period ? ` · ${req.preferred_period}` : req.preferred_time ? ` · ${req.preferred_time}` : ''}
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '700', textAlign: 'center',
                      color: (req.credits_remaining ?? 3) === 0 ? '#ff082f' : (req.credits_remaining ?? 3) === 1 ? '#f5b832' : '#166534'
                    }}>
                      {req.credits_remaining != null ? `${req.credits_remaining}/3` : '—'}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: s.bg, color: s.color, textAlign: 'center' }}>
                      {s.label}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right', display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      {/* Orange dot — Showami webhook flagged unread
                          (status transition or new agent message on a
                          previously-reviewed row). Clears when the row
                          is expanded (per-row mark-read). */}
                      {req.admin_unread && (
                        <span title="New activity from Showami"
                          style={{ width: 8, height: 8, borderRadius: 9999, background: '#f97316', display: 'inline-block' }} />
                      )}
                      {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{ borderTop: '1px solid #f3f4f6', padding: '16px 20px', backgroundColor: '#fafafa' }}>
                      {/* Contact cards — Borrower (left) + Showing Agent
                          (right). Showing-agent fields are populated by
                          the Showami webhook (showing_agent_name /
                          _email / _phone on the showing_requests row);
                          fall back to "—" until Showami assigns one. */}
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={contactCardStyle}>
                          <p style={cardTitleStyle}>Borrower</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <p style={labelStyle}>Name</p>
                              <p style={valueStyle}>
                                {[req.first_name, req.last_name].filter(Boolean).join(' ') || '—'}
                              </p>
                            </div>
                            <div>
                              <p style={labelStyle}>Email</p>
                              <p style={valueStyle}>{req.email || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Phone</p>
                              <p style={valueStyle}>{req.phone || '—'}</p>
                            </div>
                          </div>
                        </div>
                        <div style={contactCardStyle}>
                          <p style={cardTitleStyle}>Showing Agent</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <p style={labelStyle}>Name</p>
                              <p style={valueStyle}>{req.showing_agent_name || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Email</p>
                              <p style={valueStyle}>{req.showing_agent_email || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Phone</p>
                              <p style={valueStyle}>{req.showing_agent_phone || '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <p style={labelStyle}>Loan Number</p>
                          <p style={valueStyle}>{req.loan_number || '—'}</p>
                        </div>
                        <div>
                          <p style={labelStyle}>Preferred Date &amp; Time</p>
                          <p style={valueStyle}>
                            {req.preferred_date
                              ? new Date(req.preferred_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                              : 'No time given — borrower asked us to follow up'}
                            {req.preferred_period ? ` · ${req.preferred_period}` : req.preferred_time ? ` at ${req.preferred_time}` : ''}
                          </p>
                        </div>
                        {req.notes && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <p style={labelStyle}>Borrower Notes</p>
                            <p style={valueStyle}>{req.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Status is read-only here — driven by the Showami
                          webhook (status transitions land on the row via
                          backend/routes/showami.js). Admin's only
                          editable surface on this row is Admin Notes. */}
                      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '12px', alignItems: 'start', marginBottom: '12px' }}>
                        <div>
                          <p style={labelStyle}>Status</p>
                          <span style={{
                            display: 'inline-block',
                            fontSize: '12px', fontWeight: 600,
                            padding: '4px 12px', borderRadius: '9999px',
                            backgroundColor: s.bg, color: s.color,
                          }}>
                            {s.label}
                          </span>
                        </div>
                        <div>
                          <p style={labelStyle}>Admin Notes</p>
                          <textarea
                            value={edit.admin_notes || ''}
                            onChange={(e) => setEditMap((prev) => ({ ...prev, [req.id]: { ...prev[req.id], admin_notes: e.target.value } }))}
                            rows={2}
                            placeholder="Internal notes..."
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleSave(req.id)}
                          disabled={savingId === req.id}
                          style={{ padding: '7px 16px', backgroundColor: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          {savingId === req.id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/borrowers/${req.user_id}`)}
                          style={{ padding: '7px 16px', backgroundColor: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          View Borrower
                        </button>
                      </div>

                      {/* Additional details — cancellation reason
                          surfaces here when present. Agent contact
                          info moved out to the always-visible strip
                          above the expansion so the RE desk can grab
                          phone/email without expanding the row. */}
                      {req.cancelled_reason && (
                        <div style={{ marginTop: 16, padding: '12px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                            Additional Details
                          </div>
                          <div style={{ fontSize: 13, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 10px', marginTop: 6 }}>
                            <strong>Cancellation reason:</strong> {req.cancelled_reason}
                          </div>
                        </div>
                      )}

                      {/* iMessage-style messenger: outbound (Doorly)
                          right-aligned blue bubbles, inbound (agent)
                          left-aligned white bubbles. Reply box flush
                          to the bottom of the thread; Enter sends,
                          Shift+Enter inserts a newline. */}
                      <div style={{ marginTop: 20 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#111111' }}>
                          Messages with Showing Agent
                        </div>

                        {/* Scrollable message list */}
                        <div style={{
                          height: 280,
                          overflowY: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px 10px 0 0',
                          padding: 14,
                          background: '#f9fafb',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                        }}>
                          {messagesById[req.id] === undefined && (
                            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', margin: 'auto' }}>
                              Loading messages…
                            </div>
                          )}
                          {messagesById[req.id] && messagesById[req.id].length === 0 && (
                            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', margin: 'auto' }}>
                              No messages yet
                            </div>
                          )}
                          {messagesById[req.id] && messagesById[req.id].length > 0 && messagesById[req.id].map((m) => {
                            const isOutbound = m.sender_type === 'doorly';
                            const labelText = isOutbound ? 'Doorly Team' : (m.sender_name || 'Showing Agent');
                            return (
                              <div key={m.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isOutbound ? 'flex-end' : 'flex-start',
                              }}>
                                <div style={{
                                  fontSize: 11,
                                  color: '#9ca3af',
                                  marginBottom: 3,
                                  paddingLeft:  isOutbound ? 0 : 4,
                                  paddingRight: isOutbound ? 4 : 0,
                                }}>
                                  {labelText}
                                </div>
                                <div style={{
                                  background: isOutbound ? '#3a6ad9' : '#ffffff',
                                  color:      isOutbound ? '#ffffff' : '#111111',
                                  borderRadius: isOutbound ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                                  padding: '9px 13px',
                                  maxWidth: '75%',
                                  fontSize: 13,
                                  lineHeight: 1.5,
                                  border: isOutbound ? 'none' : '1px solid #e5e7eb',
                                  wordBreak: 'break-word',
                                  whiteSpace: 'pre-wrap',
                                }}>
                                  {m.message_text}
                                </div>
                                <div style={{
                                  fontSize: 10,
                                  color: '#9ca3af',
                                  marginTop: 3,
                                  paddingLeft:  isOutbound ? 0 : 4,
                                  paddingRight: isOutbound ? 4 : 0,
                                }}>
                                  {formatMessageTime(m.received_at || m.created_at)}
                                </div>
                              </div>
                            );
                          })}
                          <div ref={messagesBottomRef} />
                        </div>

                        {/* Reply input — flush to the bottom of the thread */}
                        <div style={{
                          border: '1px solid #e5e7eb',
                          borderTop: 'none',
                          borderRadius: '0 0 10px 10px',
                          background: '#ffffff',
                          padding: 10,
                          display: 'flex',
                          gap: 8,
                          alignItems: 'flex-end',
                        }}>
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendShowingMessage();
                              }
                            }}
                            onInput={(e) => {
                              // Auto-expand up to 100px before scrolling.
                              e.target.style.height = 'auto';
                              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                            }}
                            placeholder="Message the showing agent..."
                            rows={1}
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              resize: 'none',
                              fontSize: 13,
                              fontFamily: 'inherit',
                              lineHeight: 1.5,
                              padding: '4px 0',
                              background: 'transparent',
                              maxHeight: 100,
                              overflowY: 'auto',
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleSendShowingMessage}
                            disabled={!replyText.trim() || sendingReply}
                            style={{
                              background: replyText.trim() && !sendingReply ? '#3a6ad9' : '#e5e7eb',
                              color:      replyText.trim() && !sendingReply ? '#ffffff' : '#9ca3af',
                              border: 'none',
                              borderRadius: 8,
                              padding: '7px 14px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: replyText.trim() && !sendingReply ? 'pointer' : 'not-allowed',
                              transition: 'background 0.15s',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {sendingReply ? 'Sending…' : 'Send'}
                          </button>
                        </div>

                        {replyError && (
                          <div style={{ fontSize: 12, color: '#ff082f', marginTop: 6 }}>
                            {replyError}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                          Press Enter to send · Shift+Enter for new line · Message delivered to agent in Showami
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

    </AdminLayout>
  );
}

const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' };
const valueStyle = { fontSize: '13px', color: '#111827', margin: 0 };
const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#111827', backgroundColor: '#fff', boxSizing: 'border-box' };
const contactCardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' };
const cardTitleStyle = { fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 10px', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6' };
